import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { InputSize, Stack } from '@lyearn/ui';
import NumberInput from '@/components/NumberInput';

import { hourInputStyles } from './styles';

const MULTIPLIER = 60;
const MAX_HOUR = Math.floor(Number.MAX_SAFE_INTEGER / (MULTIPLIER * MULTIPLIER)) - 1; // to avoing integer overflow

/**
 * @expects value = 1800 (30 * 60) // expects duration in seconds
 * @returns value = 1800 (30 * 60) // return duration in seconds
 * @displays hours = 0 minutes = 30 // duration in hours and minutes
 */

function Duration({
  error,
  value: valueInSeconds,
  onChange,
  numberInputStyles = 'w-[15rem]',
  className,
  disabled,
  size,
}: {
  error?: boolean;
  value: number;
  onChange: (value: number) => void;
  numberInputStyles?: string;
  className?: string;
  disabled?: boolean;
  size?: InputSize;
}) {
  const { t } = useTranslation();
  const valueInMinutes = valueInSeconds / MULTIPLIER;
  const [hours, minutes] = [Math.floor(valueInMinutes / MULTIPLIER), valueInMinutes % MULTIPLIER];

  const _onChange = useCallback(
    (updatedHour: number, updatedMinutes: number) => {
      onChange((updatedHour * MULTIPLIER + updatedMinutes) * MULTIPLIER);
    },
    [onChange],
  );

  const _onChangeHours = useCallback(
    (newValue: number) => {
      const updatedHour = Number.isInteger(newValue) ? newValue : 0;
      _onChange(updatedHour, minutes);
    },
    [_onChange, minutes],
  );

  const _onChangeMinutes = useCallback(
    (newValue: number) => {
      const updatedMinutes = Number.isInteger(newValue) ? newValue : 0;
      _onChange(hours, updatedMinutes);
    },
    [_onChange, hours],
  );

  return (
    <Stack alignItems="center" className={className} gapX={8}>
      <NumberInput
        canBeEmpty
        className={cn(numberInputStyles, hourInputStyles)}
        disabled={disabled}
        error={error}
        inputProps={{ style: { width: `${hours.toString().length}.1ch` } }}
        inputSuffix={t('Common:hrs')}
        max={MAX_HOUR}
        min={0}
        size={size}
        value={hours}
        onChange={_onChangeHours}
      />
      <NumberInput
        canBeEmpty
        className={numberInputStyles}
        disabled={disabled}
        error={error}
        inputSuffix={t('Common:mins')}
        max={59}
        min={0}
        size={size}
        value={minutes}
        onChange={_onChangeMinutes}
      />
    </Stack>
  );
}

export default React.memo(Duration);
